import {t} from "@lingui/core/macro";
import {compose} from "mu-lambda";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {dismiss, isDismissed} from "@khan/component-dismissible";
import Log from "@khan/logging";

import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import withGql, {
    type WithGqlProps,
    type WithoutGql,
} from "#data-access/gql/with-gql.tsx";

import type {WelcomeStates} from "../homepage-data.ts";

const StudentMasteryGoalList = React.lazy(
    () =>
        import(
            "../../content-library-legacy/modules/student-mastery-goal/list.tsx"
        ),
);

type OwnProps = {
    classId: string;
    classTitle: string;
    coachKaid: string;
    classDescriptor: string;
    isMap: boolean;
    welcomeState: WelcomeStates;
    kaid: string;
    onSufficientlyUsable: () => void;
    currentAssessmentTestKey?: string;
    gradeLevel?: string;
};

type Props = OwnProps & WithMarkConversionProps & WithGqlProps;

type State = {
    showWelcomeBanner: boolean;
    showMultiAccountBanner: boolean;
};

class Goals extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_isMounted' has no initializer and is not definitely assigned in the constructor.
    _isMounted: boolean;

    state: State = {
        showWelcomeBanner: false,
        showMultiAccountBanner: false,
    };

    componentDidMount() {
        const {markConversion} = this.props;

        this.maybeShowBanners();
        markConversion({id: "logged_in_homepage_visit"});

        this._isMounted = true;
    }

    componentDidUpdate(prevProps: Props) {
        const isEqual = <T extends keyof Props>(
            getter: (props: Props) => Props[T],
        ) => getter(this.props) === getter(prevProps);

        if (
            !isEqual((p) => p.welcomeState) ||
            !isEqual((p) => p.classId) ||
            !isEqual((p) => p.coachKaid)
        ) {
            this.maybeShowBanners();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    maybeShowBanners() {
        const {welcomeState, gqlFetch} = this.props;

        // We set `showWelcomeBanner` to `false` here because we have no idea if we
        // want to show it yet, and there is a chance that the `isDismissed`
        // request fails.  If at this point `showWelcomeBanner` is set to `true` and
        // after we query for the dismissal key it is supposed to be `false` and
        // the query fails it will incorrectly show.
        this.setState({showWelcomeBanner: false});
        this.setState({showMultiAccountBanner: false});

        if (welcomeState) {
            isDismissed(gqlFetch, this.generateDismissalKey("welcome"))
                .then((dismissed) => {
                    if (!this._isMounted) {
                        return;
                    }

                    // If we've have not yet dismissed the banner then show it.
                    this.setState({showWelcomeBanner: !dismissed});
                })
                .catch(() => {
                    Log.log("Failed to determine dismissal state in Goals");
                });

            isDismissed(gqlFetch, this.generateDismissalKey("multi"))
                .then((dismissed) => {
                    if (!this._isMounted) {
                        return;
                    }

                    // If we've have not yet dismissed the banner then show it.
                    this.setState({showMultiAccountBanner: !dismissed});
                })
                .catch(() => {
                    Log.log("Failed to determine dismissal state in Goals");
                });
        }
    }

    handleWelcomeBannerClose: () => void = () => {
        dismiss(
            this.props.gqlFetch,
            this.generateDismissalKey("welcome"),
            "never",
        );
        this.setState({showWelcomeBanner: false});
    };

    handleMultiAccountBannerClose: () => void = () => {
        dismiss(
            this.props.gqlFetch,
            this.generateDismissalKey("multi"),
            "never",
        );
        this.setState({showMultiAccountBanner: false});
    };

    generateDismissalKey(key: string): string {
        const {classId, coachKaid} = this.props;

        return `welcome_to_k4d_banner_${coachKaid}_${classId}_${key}`;
    }

    render(): React.ReactNode {
        const {
            classId,
            classTitle,
            coachKaid,
            classDescriptor,
            isMap,
            welcomeState,
            onSufficientlyUsable,
            currentAssessmentTestKey,
            gradeLevel,
        } = this.props;
        const flag = true;

        let title: string;
        if (isMap) {
            title = t`My Learning Paths`;
        } else if (flag) {
            title = t`My mastery goals`;
        } else {
            title = t`My course mastery`;
        }

        if (classDescriptor === "" || classDescriptor === null) {
            return null;
        }

        return (
            <View testId={title}>
                <React.Suspense fallback={<RouteLoadSpinner />}>
                    <StudentMasteryGoalList
                        allowPast={true}
                        classId={classId}
                        coachKaid={coachKaid}
                        classDescriptor={classDescriptor}
                        isMapGoal={isMap}
                        welcomeState={welcomeState}
                        classTitle={classTitle}
                        title={title}
                        showWelcomeBanner={this.state.showWelcomeBanner}
                        showMultiAccountBanner={
                            this.state.showMultiAccountBanner
                        }
                        onWelcomeBannerClosed={this.handleWelcomeBannerClose}
                        onMultiAccountBannerClosed={
                            this.handleMultiAccountBannerClose
                        }
                        onCompleted={onSufficientlyUsable}
                        currentAssessmentTestKey={currentAssessmentTestKey}
                        gradeLevel={gradeLevel}
                    />
                </React.Suspense>
            </View>
        );
    }
}

type ExportProps = WithoutGql<
    WithoutMarkConversion<
        JSX.LibraryManagedAttributes<
            typeof Goals,
            React.ComponentProps<typeof Goals>
        >
    >
>;

const GoalsWithLearningTime = compose(
    withMarkConversion,
    withGql,
)(Goals) as React.ComponentType<ExportProps>;
export default GoalsWithLearningTime;
